// ============================================================================
// @gs/ui/branding/BrandingProvider — theming runtime par company (ADR-059)
// ============================================================================
// Au login, fetch GET /api/branding (createBrandingRoute de @gs/api-shared),
// dérive l'échelle 50..900 du hex `colorPrimary` (deriveBrandScale, ratios
// figés) et surcharge les variables CSS --brand-N-rgb sur <html>. Les ~130
// usages Tailwind `brand-*` résolvent alors la marque du tenant.
//
// Fail-soft ABSOLU : échec réseau / 404 / hex invalide = no-op silencieux
// (console.debug seulement) → le fallback :root (GS 2026) reste en place.
// Le branding ne casse JAMAIS une app.
//
// Pilote : Console UG uniquement (rollout autres apps = BLITZ suivant après
// validation visuelle David). Les apps SANS provider gardent le fallback :root
// et le comportement actuel de GsSidebar (props headerLogoSrc/...Dark).

import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '@gs/api-client';
import { deriveBrandScale, hexToRgbChannels, BRAND_SCALE_STEPS } from './deriveBrandScale';

interface BrandingAssetDto {
  family: 'Icon' | 'Logo';
  variant: 'Black' | 'White' | 'Light_Background' | 'Dark_Background' | 'Red_Background';
  url: string;
}
interface BrandingDto {
  companyName: string;
  colorPrimary: string;
  colorSecondary: string | null;
  assets: BrandingAssetDto[];
}

export interface BrandingLogos {
  /** Icône (carrée, sidebar) — standard : Light_Background en clair, Dark_Background en sombre. */
  iconLight?: string;
  iconDark?: string;
  /** Logo complet (horizontal) — mêmes variantes. */
  logoLight?: string;
  logoDark?: string;
}

export interface BrandingContextValue {
  companyName: string;
  logos: BrandingLogos;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

/**
 * Branding du tenant courant, ou `null` si aucun BrandingProvider n'est monté
 * ou si le branding n'est pas (encore) résolu — les consommateurs (GsSidebar)
 * retombent alors sur leur comportement actuel.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook colocalisé avec son provider (pattern context standard), HMR full-reload acceptable
export function useBranding(): BrandingContextValue | null {
  return useContext(BrandingContext);
}

function pickAsset(assets: BrandingAssetDto[], family: BrandingAssetDto['family'], variant: BrandingAssetDto['variant']): string | undefined {
  return assets.find((a) => a.family === family && a.variant === variant)?.url;
}

function applyCssVariables(dto: BrandingDto): void {
  const scale = deriveBrandScale(dto.colorPrimary); // throw si hex invalide → catch appelant
  const root = document.documentElement.style;
  for (const step of BRAND_SCALE_STEPS) {
    root.setProperty(`--brand-${step}-rgb`, scale[step]);
  }
  if (dto.colorSecondary) {
    root.setProperty('--brand-ink-rgb', hexToRgbChannels(dto.colorSecondary));
  }
}

function clearCssVariables(): void {
  const root = document.documentElement.style;
  for (const step of BRAND_SCALE_STEPS) {
    root.removeProperty(`--brand-${step}-rgb`);
  }
  root.removeProperty('--brand-ink-rgb');
}

/**
 * Swap favicon tenant-aware (CHORE-FAVICONS-UNIFORMES 2026-06-11) : remplace le
 * href de tous les link[rel="icon"] par l'asset Icon/Light_Background du tenant
 * (ex: le losange UG dans l'onglet de la Console pour un corporate). Fail-soft
 * ABSOLU : pas de link existant / DOM indisponible / erreur = no-op, le favicon
 * statique GS (jeu sync:assets) reste intact. Retourne la fonction de
 * restauration (symétrique de clearCssVariables) ou null si rien n'a été fait.
 */
function applyFaviconSwap(url: string): (() => void) | null {
  try {
    const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'));
    if (links.length === 0) return null;
    const originals = links.map((link) => ({
      link,
      href: link.getAttribute('href'),
      type: link.getAttribute('type'),
    }));
    for (const link of links) {
      link.setAttribute('href', url);
      // L'asset tenant est servi sans extension garantie — laisser le navigateur sniffer.
      link.removeAttribute('type');
    }
    return () => {
      try {
        for (const { link, href, type } of originals) {
          if (href !== null) link.setAttribute('href', href);
          if (type !== null) link.setAttribute('type', type);
        }
      } catch {
        // Restauration best-effort — jamais bloquant.
      }
    };
  } catch {
    return null;
  }
}

export interface BrandingProviderProps {
  children: React.ReactNode;
  /** Fetch seulement quand l'auth est prête (ex: enabled={!!user}). Défaut true. */
  enabled?: boolean;
  /** Endpoint du branding (créé par createBrandingRoute). */
  endpoint?: string;
}

export function BrandingProvider({ children, enabled = true, endpoint = '/api/branding' }: BrandingProviderProps) {
  const [value, setValue] = useState<BrandingContextValue | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let restoreFavicon: (() => void) | null = null;
    void api
      .get<BrandingDto>(endpoint)
      .then((dto) => {
        if (cancelled || !dto?.colorPrimary) return;
        applyCssVariables(dto);
        const iconLight = pickAsset(dto.assets, 'Icon', 'Light_Background');
        // Swap favicon tenant-aware : seulement si l'asset Icon existe (fail-soft).
        if (iconLight) restoreFavicon = applyFaviconSwap(iconLight);
        setValue({
          companyName: dto.companyName,
          logos: {
            iconLight,
            iconDark: pickAsset(dto.assets, 'Icon', 'Dark_Background'),
            logoLight: pickAsset(dto.assets, 'Logo', 'Light_Background'),
            logoDark: pickAsset(dto.assets, 'Logo', 'Dark_Background'),
          },
        });
      })
      .catch((err: unknown) => {
        // Fail-soft : fallback :root (GS 2026). Debug seulement — jamais bloquant.
        console.debug('[branding] fallback :root —', (err as Error)?.message ?? err);
      });
    return () => {
      cancelled = true;
      clearCssVariables();
      restoreFavicon?.();
      setValue(null);
    };
  }, [enabled, endpoint]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}
